import { register } from '../../../JavaScript/Utils'

interface SetFocusOptions {
  focus?: boolean;
}

function debounce(func: (...args: any[]) => any, wait: number) {
  let timeout: number | null = null;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout as number);  // use type assertion here
      func(...args);
    };
    if (timeout !== null) {
      clearTimeout(timeout as number);  // and here
    }
    timeout = window.setTimeout(later, wait);
  };
}
class Tabs {
  private tablistNode: HTMLElement;
  private tabs: HTMLElement[];
  private firstTab: HTMLElement | null;
  private lastTab: HTMLElement | null;
  private tabpanels: HTMLElement[];
  private imgpanels: HTMLElement[];
  constructor(groupNode:HTMLElement) {
    this.tablistNode = groupNode;
    this.tabs = Array.from(this.tablistNode.querySelectorAll('.js_tabs-pagination-tab')) as HTMLElement[];

    this.firstTab = null;
    this.lastTab = null;

    this.tabpanels = [];
    this.imgpanels = [];

    this.setContainerHeight = debounce(this.setContainerHeight.bind(this), 300);

    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i];
      const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));
      const imgpanel = document.getElementById(tab.getAttribute('data-controls'));

      tab.tabIndex = -1;
      tab.setAttribute('aria-selected', 'false');

      this.tabpanels.push(tabpanel);

      if(imgpanel) {
        this.imgpanels.push(imgpanel);
      }

      tab.addEventListener('keydown', this.onKeydown.bind(this));
      tab.addEventListener('click', this.onClick.bind(this));

      if (!this.firstTab) {
        this.firstTab = tab;
      }
      this.lastTab = tab;
    }

    if (this.firstTab) {
      this.setSelectedTab(this.firstTab, { focus: false });
    }

    if(document.documentElement.clientWidth >= 1024) this.setContainerHeight();

    window.addEventListener('resize', this.setContainerHeight);

    window.addEventListener('hashchange', this.handleHashChange.bind(this), false);

    // Also, check for the hash when the component is initialized
    this.handleHashChange.bind(this);
  }

  setContainerHeight() {
    const children = this.tabpanels;
    const container = children[0].parentElement;
    let maxHeight = 0;

    if(document.documentElement.clientWidth < 1024) {
      container.style.height = '';
      return;
    }

    children.forEach(child => {
      const childCurrentHeight = child.offsetTop + child.offsetHeight;
      if (childCurrentHeight > maxHeight) {
        maxHeight = childCurrentHeight;
      }
    });

    container.style.height = `${maxHeight}px`;
  }

  setSelectedTab(currentTab: HTMLElement, options: SetFocusOptions = { focus: true }) {
    const { focus } = options;

    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i];
      if (currentTab === tab) {
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');
        this.tabpanels[i].removeAttribute('inert');
        this.tabpanels[i].classList.remove('is-hidden');

        if(this.imgpanels.length > 0) {
          this.imgpanels[i].classList.remove('is-hidden');
          this.imgpanels[i].classList.add('is-active');
        }

        if (focus) {
          tab.focus();
        }
      } else {
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        this.tabpanels[i].setAttribute('inert','true')
        this.tabpanels[i].classList.add('is-hidden');

        if(this.imgpanels.length > 0) {
          this.imgpanels[i].classList.add('is-hidden');
          this.imgpanels[i].classList.remove('is-active');
        }
      }
    }
  }

  setSelectedToPreviousTab(currentTab:any) {
    let index;

    if (currentTab === this.firstTab) {
      this.setSelectedTab(this.lastTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.setSelectedTab(this.tabs[index - 1]);
    }
  }

  setSelectedToNextTab(currentTab:any) {
    let index;

    if (currentTab === this.lastTab) {
      this.setSelectedTab(this.firstTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.setSelectedTab(this.tabs[index + 1]);
    }
  }

  /* EVENT HANDLERS */

  handleHashChange() {
    const hash = window.location.hash.substring(1);

    for (const tab of this.tabs) {
      if (tab.id === hash) {
        this.setSelectedTab(tab, { focus: false });

        history.replaceState(null, null, ' ');

        break;
      }
    }
  }

  onKeydown(event: KeyboardEvent) {
    const tgt = event.currentTarget;
    let flag = false;

    switch (event.key) {
      case 'ArrowLeft':
        this.setSelectedToPreviousTab(tgt);
        flag = true;
        break;

      case 'ArrowRight':
        this.setSelectedToNextTab(tgt);
        flag = true;
        break;

      case 'Home':
        this.setSelectedTab(this.firstTab);
        flag = true;
        break;

      case 'End':
        this.setSelectedTab(this.lastTab);
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  onClick(event:any) {
    this.setSelectedTab(event.currentTarget);
    this.setSelectedTab(event.currentTarget);
  }
}

export const Tabelement = (el: HTMLElement) => {
  const tablists = el.querySelectorAll('.js_tabs-pagination');
  tablists.forEach(tabsContainer => new Tabs(tabsContainer as HTMLElement));
};

register('Tabelement', Tabelement)
